import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import TrainerSearch from "./pages/TrainerSearch";
import TrainerProfile from "./pages/TrainerProfile";
import Sessions from "./pages/Sessions";
import Challenges from "./pages/Challenges";
import Safety from "./pages/Safety";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import AdminSafety from "./pages/AdminSafety";
// AdminWaitlist removed (Jun 2026)
import MySessions from "./pages/MySessions";
import SessionWaiting from "./pages/SessionWaiting";
import { PaymentSuccess, PaymentCancel } from "./pages/Payment";
import Checkout from "./pages/Checkout";
import PackageCheckout from "./pages/PackageCheckout";
import BecomeATrainer from "./pages/BecomeATrainer";
import MessagesPage from "./pages/MessagesPage";
import HowItWorks from "./pages/HowItWorks";
import AdminDashboard from "./pages/AdminDashboard";
import DisciplineInfo from "./pages/DisciplineInfo";
import Earnings from "./pages/Earnings";
import MyListings from "./pages/MyListings";
import Profile from "./pages/Profile";
import ClientOnboardingQuiz from "./components/ClientOnboardingQuiz";
import EmergencySOS from "./components/EmergencySOS";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <div className="spinner" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// App Routes Component
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout><Landing /></Layout>} />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Semi-Protected Routes (viewable but actions need auth) */}
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/terms" element={<Layout><Terms /></Layout>} />
      <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
      <Route path="/trainers" element={<Layout><TrainerSearch /></Layout>} />
      <Route path="/find-trainer" element={<Layout><TrainerSearch /></Layout>} />
      <Route path="/map" element={<Layout><TrainerSearch /></Layout>} />
      <Route path="/trainers/:id" element={<Layout><TrainerProfile /></Layout>} />
      <Route path="/become-a-trainer" element={<Layout><BecomeATrainer /></Layout>} />
      <Route path="/how-it-works" element={<Layout><HowItWorks /></Layout>} />
      <Route path="/disciplines/:slug" element={<Layout><DisciplineInfo /></Layout>} />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sessions" 
        element={
          <ProtectedRoute>
            <Layout><Sessions /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/dashboard/sessions"
        element={<ProtectedRoute><Layout><Sessions /></Layout></ProtectedRoute>}
      />
      <Route
        path="/dashboard/earnings"
        element={<ProtectedRoute><Layout><Earnings /></Layout></ProtectedRoute>}
      />
      <Route
        path="/earnings"
        element={<ProtectedRoute><Layout><Earnings /></Layout></ProtectedRoute>}
      />
      <Route
        path="/dashboard/listings"
        element={<ProtectedRoute><Layout><MyListings /></Layout></ProtectedRoute>}
      />
      <Route
        path="/profile"
        element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>}
      />
      <Route
        path="/account"
        element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>}
      />
      <Route
        path="/messages"
        element={<ProtectedRoute><Layout><MessagesPage /></Layout></ProtectedRoute>}
      />
      <Route
        path="/admin"
        element={<ProtectedRoute><Layout><AdminDashboard /></Layout></ProtectedRoute>}
      />
      <Route 
        path="/sessions/:sessionId/wait" 
        element={
          <ProtectedRoute>
            <Layout><SessionWaiting /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/challenges" 
        element={
          <ProtectedRoute>
            <Layout><Challenges /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/safety" 
        element={
          <ProtectedRoute>
            <Layout><Safety /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/safety"
        element={
          <ProtectedRoute>
            <Layout><AdminSafety /></Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Payment Routes */}
      <Route 
        path="/checkout" 
        element={
          <ProtectedRoute>
            <Layout><Checkout /></Layout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/checkout/package"
        element={
          <ProtectedRoute>
            <Layout><PackageCheckout /></Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/payment/success" element={<Layout><PaymentSuccess /></Layout>} />
      <Route path="/payment/cancel" element={<Layout><PaymentCancel /></Layout>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const toasterOptions = {
  style: {
    background: '#18181B',
    border: '1px solid #27272A',
    color: '#FAFAFA',
  },
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <ClientOnboardingQuiz />
          <EmergencySOS />
          <Toaster
            position="top-right"
            richColors
            toastOptions={toasterOptions}
          />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
